import React from 'react';
import { BasicForm, notification, IBasicTableProps, AdvanceTable, TextAreaFieldItem, Typography } from 'ui-lib';
import { CheckboxGroupItem, TextFieldItem, IconTextButton, useForm, } from 'ui-lib';
import fileTransferCss from './new-file-transfer.css';
import { useDeviceGroupsStore, useStores } from 'store';
import { getErrorMessage } from 'common-utils';
import { confirmAction } from 'pages/private/admin/utils';
import { FileTransferDto } from 'dto/file-transfer.dto';
import { basicColumns, timeColumns, TRANSFER_OPTION_VALUE, TRANSFER_OPTIONS } from './transfer-files-tab';

interface ISendFilesProps {
    selectedClientAccountId: number;
    selectedDeviceGroup: string;
    selectedFileTransfers: FileTransferDto[];
    onCancel?: () => void;
}

export const SendFiles = (props: ISendFilesProps) => {
    const stores = useStores();
    const deviceGroupsStore = useDeviceGroupsStore();
    const [form] = useForm();
    const [optionsCheckedList, setOptionsCheckedList] = React.useState([1, 2]);
    const [description, setDescription] = React.useState('');

    React.useEffect(() => {
        deviceGroupsStore.loadDeviceGroupDetails();
    }, []);

    const deviceGroupLabel = React.useMemo(() => {
        return deviceGroupsStore.getDeviceGroupByName(props.selectedDeviceGroup)?.deviceGroupLabel
    }, [props.selectedDeviceGroup]);

    const uploadFilesTable: IBasicTableProps = {
        dataSource: props.selectedFileTransfers || [],
        columns: [...basicColumns, ...timeColumns],
    };

    const transferFiles = async (values: any) => {
        try {
            const result = await deviceGroupsStore.transferFiles(
                props.selectedClientAccountId,
                props.selectedDeviceGroup,
                props.selectedFileTransfers.map(file => file.uuid),
                optionsCheckedList.includes(TRANSFER_OPTION_VALUE.USE_OUT_OF_SERVICE_WINDOWS),
                optionsCheckedList.includes(TRANSFER_OPTION_VALUE.REQUEST_REBOOT),
                description,
                values.shellCommandField,
            );

            if (result) {
                notification.success({
                    message: 'Transfer Files Successfully',
                    description: `Files have been transferred to the device group ${deviceGroupLabel}.`,
                });
                props.onCancel();
            }
        } catch (err) {
            notification.error({
                message: getErrorMessage(err),
                placement: 'topRight',
            });
        }
    };

    const handleSubmit = (values: any) => {
        confirmAction(values, 'Transfer Files', 'Do you want to transfer files', transferFiles);
    };

    return (
        <React.Fragment>
            <Typography.Title level={2}>Send Files to Device Group</Typography.Title>
            <BasicForm
                title='Send Files'
                form={form}
                onSubmit={handleSubmit}
                className={fileTransferCss['transfer-form']}
                items={[
                    <TextFieldItem
                        key='deviceGroup'
                        code='deviceGroup'
                        label='Device Group'
                        colon={true}
                        labelAlign='right'
                        initialValue={deviceGroupLabel}
                        disabled={true}
                    />,
                    <TextAreaFieldItem
                        labelColSpan={5}
                        code='shellCommandField'
                        label='Preparation script'
                        colon={true}
                        labelAlign='right'
                        isRequired={false}
                        textAreaProps={{
                            placeholder: 'Here you can specify a script or command BEFORE processing downloaded file. Ex.\n#!/bin/bash\nserivce afc-app stop',
                            autoSize: { minRows: 10, maxRows: 200 },
                        }}
                    />,
                    <TextFieldItem
                        key='description'
                        code='description'
                        label='Description'
                        colon={true}
                        labelAlign='right'
                        initialValue={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />,
                    <CheckboxGroupItem
                        labelAlign='right'
                        key='options'
                        code='options'
                        defaultValue={optionsCheckedList}
                        label='Options'
                        colon={true}
                        options={TRANSFER_OPTIONS}
                        value={optionsCheckedList}
                        onChange={(value) => setOptionsCheckedList(value)}
                    />,
                    <AdvanceTable
                        table={uploadFilesTable}
                        title={''}
                    />,
                    <div className={fileTransferCss['row']}>
                        <IconTextButton
                            key='back-button'
                            className={fileTransferCss['footer-left-button']}
                            htmlType='button'
                            label='Back'
                            onClick={props.onCancel}
                        />
                        <IconTextButton
                            key='send-submit-button'
                            className={fileTransferCss['footer-right-button']}
                            htmlType='submit'
                            label='Send'
                            type='primary'
                        />
                    </div>,
                ]}
            />
        </React.Fragment>
    );
};

